import React, { FC, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import style from './PartialsStyle.module.css';
import {
  TEL1_MAX_LENGTH,
  TEL2_MAX_LENGTH,
  TEL3_MAX_LENGTH,
} from '@/hooks/api/carWash/useCarWashConditionerStep2';

const TEL1_INDEX = 0;
const TEL2_INDEX = 1;
const TEL3_INDEX = 2;

type PhoneInputProps = {
  type: 'phone_1' | 'phone_2';
  title: string;
  error: string;
  required?: boolean;
};

type Step2FormData = {
  tel1?: string;
  tel2?: string;
  tel3?: string;
  tel4?: string;
  tel5?: string;
  tel6?: string;
};

export const PhoneInput: FC<PhoneInputProps> = ({
  type,
  title,
  error,
  required = false,
}) => {
  const isComposing = useRef<boolean>(false);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const { watch, setValue, trigger } = useFormContext<Step2FormData>();

  const tel1 = watch(type === 'phone_1' ? 'tel1' : 'tel4') || '';
  const tel2 = watch(type === 'phone_1' ? 'tel2' : 'tel5') || '';
  const tel3 = watch(type === 'phone_1' ? 'tel3' : 'tel6') || '';

  useEffect(() => {
    if (type === 'phone_1' && tel1 && tel2 && tel3) {
      trigger('tel1');
      trigger('tel2');
      trigger('tel3');
    } else if (type === 'phone_2' && tel1 && tel2 && tel3) {
      trigger('tel4');
      trigger('tel5');
      trigger('tel6');
    }
  }, [tel1, tel2, tel3, trigger, type]);

  const normalizeValue = (value: string, maxLength: number) => {
    const halfWidth = value.replace(/[０-９]/g, (s) =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0),
    );
    return halfWidth.replace(/\D/g, '').slice(0, maxLength);
  };

  const handleInputChange = (
    index: number,
    fieldName: keyof Step2FormData,
    value: string,
    maxLength: number,
  ) => {
    if (isComposing.current) return;

    const normalized = normalizeValue(value, maxLength);
    setValue(fieldName, normalized, {
      shouldValidate: true,
      shouldTouch: true,
    });

    if (normalized.length === maxLength) {
      if (index < 2) {
        inputRefs[index].current?.blur();
        inputRefs[index + 1].current?.focus();
      } else {
        inputRefs[index].current?.blur();
      }
    }
  };

  const handleCompositionEnd = (
    index: number,
    fieldName: keyof Step2FormData,
    value: string,
    maxLength: number,
  ) => {
    isComposing.current = false;
    handleInputChange(index, fieldName, value, maxLength);
  };

  return (
    <div className={`${style['input-group']}`}>
      <div
        className={`${style['input-text']} ${
          !!error ? style['input-text-error'] : ''
        }`}
      >
        <label>
          {title}
          <span
            className={`${style['input-label-tips']} ${
              required && style['required']
            }`}
          >
            {required ? '必須' : '任意'}
          </span>
        </label>
        <div className={style['input-group-1']}>
          <input
            ref={inputRefs[TEL1_INDEX]}
            placeholder="000"
            type="text"
            inputMode="numeric"
            maxLength={3}
            value={tel1}
            onCompositionStart={() => (isComposing.current = true)}
            onCompositionEnd={(e) =>
              handleCompositionEnd(
                TEL1_INDEX,
                type === 'phone_1' ? 'tel1' : 'tel4',
                e.currentTarget.value,
                TEL1_MAX_LENGTH,
              )
            }
            onChange={(e) =>
              handleInputChange(
                TEL1_INDEX,
                type === 'phone_1' ? 'tel1' : 'tel4',
                e.target.value,
                TEL1_MAX_LENGTH,
              )
            }
          />
          -
          <input
            ref={inputRefs[TEL2_INDEX]}
            placeholder="1234"
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={tel2}
            onCompositionStart={() => (isComposing.current = true)}
            onCompositionEnd={(e) =>
              handleCompositionEnd(
                TEL2_INDEX,
                type === 'phone_1' ? 'tel2' : 'tel5',
                e.currentTarget.value,
                TEL2_MAX_LENGTH,
              )
            }
            onChange={(e) =>
              handleInputChange(
                TEL2_INDEX,
                type === 'phone_1' ? 'tel2' : 'tel5',
                e.target.value,
                TEL2_MAX_LENGTH,
              )
            }
          />
          -
          <input
            ref={inputRefs[TEL3_INDEX]}
            placeholder="5678"
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={tel3}
            onCompositionStart={() => (isComposing.current = true)}
            onCompositionEnd={(e) =>
              handleCompositionEnd(
                TEL3_INDEX,
                type === 'phone_1' ? 'tel3' : 'tel6',
                e.currentTarget.value,
                TEL3_MAX_LENGTH,
              )
            }
            onChange={(e) =>
              handleInputChange(
                TEL3_INDEX,
                type === 'phone_1' ? 'tel3' : 'tel6',
                e.target.value,
                TEL3_MAX_LENGTH,
              )
            }
          />
        </div>
        {!!error && (
          <p className={style['input-error-message']}>
            有効な電話番号が指定されていません
          </p>
        )}
      </div>
    </div>
  );
};
